import React, { useState, useCallback } from "react";
import {
  Combobox,
  TextInput,
  Loader,
  useCombobox,
  CloseButton,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { IconMapPin, IconCurrentLocation } from "@tabler/icons-react";

import { useAddressAutocomplete } from "@/hooks/useAddressAutocomplete";
import { GeoCoords } from "@/hooks/useGeolocation";
import { AddressData } from "@/hooks/useGoogleMapsAutocomplete";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";
import { useLocationHistoryContext } from "@/contexts/LocationHistoryContext";

import { AddressPickerModal } from "./AddressPickerModal";

// ============================================
// Constants
// ============================================

/** Minimum address length worth geocoding on blur */
const MIN_GEOCODE_LENGTH = 5;

/** Minimum characters to trigger autocomplete search */
const MIN_SEARCH_LENGTH = 3;


// ============================================
// Types
// ============================================

export interface AddressAutocompleteInputProps {
  /** Current input value */
  value: string;

  /**
   * Callback when value changes.
   *
   * Called with fullAddress in these cases:
   * - User selects from dropdown → fullAddress with coordinates
   * - User uses locate button → fullAddress with coordinates
   * - User blurs after manual edit → fullAddress from forward geocoding (or null if geocode fails)
   *
   * Called with null fullAddress when:
   * - User is actively typing (each keystroke)
   * - User clears the field
   *
   * Parents should clear their stored coordinates when fullAddress is null,
   * and set them when fullAddress is provided.
   */
  onChange: (address1: string, fullAddress: AddressData | null) => void;

  /** Custom addresses to show at the top of suggestions (e.g., saved addresses) */
  customAddresses?: AddressData[];

  /** Location bias for relevant nearby results */
  locationBias?: GeoCoords | null;

  /** Placeholder text */
  placeholder?: string;

  /** Input label */
  label?: string;

  /** Input size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Error message */
  error?: string | null | undefined;

  /** Whether field is required */
  required?: boolean;

  /** Whether field is disabled */
  disabled?: boolean;

  /** Callback when input is focused (useful for requesting geolocation) */
  onFocus?: () => void;

  /** Whether to show the "Use my location" button */
  showLocateButton?: boolean;

  /** Tooltip text for locate button */
  locateButtonTooltip?: string;

  /** Whether to render dropdown in a portal (fixes issues inside modals/drawers) */
  withinPortal?: boolean;
}

// ============================================
// Component
// ============================================

export const AddressAutocompleteInput: React.FC<
  AddressAutocompleteInputProps
> = (props) => {
  const { isMobile } = useAppEnvironment();

  if (isMobile) {
    return <AddressAutocompleteMobile {...props} />;
  }

  return <AddressAutocompleteDesktop {...props} />;
};

// ============================================
// Mobile Sub-Component: Read-only input + full-screen modal
// ============================================

const AddressAutocompleteMobile: React.FC<AddressAutocompleteInputProps> = ({
  value,
  onChange,
  customAddresses = [],
  locationBias = null,
  placeholder = "Search for an address",
  label = "Address",
  size = "md",
  error,
  required,
  disabled,
  onFocus,
  showLocateButton = true,
  locateButtonTooltip = "common:buttons.useMyLocation",
}) => {
  const { t } = useTranslation(["common"]);
  const [modalOpened, setModalOpened] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  const {
    requestLocation,
    isLoading: locationLoading,
    permissionDenied,
    isSupported: geoSupported,
  } = useLocationHistoryContext();

  /**
   * Handle address selection from the modal.
   * This is called when the user picks from suggestions, uses "Use ...",
   * uses locate, or when the modal's close handler forward-geocodes typed text.
   */
  const handleModalSelect = useCallback(
    (address1: string, fullAddress: AddressData | null) => {
      onChange(address1, fullAddress);
    },
    [onChange]
  );

  /**
   * Handle modal close (without selection).
   * The modal itself handles forward-geocoding typed text on close,
   * so we just need to close our state here.
   */
  const handleModalClose = useCallback(() => {
    setModalOpened(false);
  }, []);

  /**
   * Handle clear button on the read-only input.
   * Clears value and explicitly notifies parent with null fullAddress
   * so the parent can clear its stored coordinates / location preview.
   */
  const handleClear = useCallback(() => {
    onChange("", null);
  }, [onChange]);

  /**
   * Request geolocation proactively when the modal opens.
   * This ensures location bias is available for Google suggestions
   * without requiring the user to tap the locate button.
   */
  const handleRequestGeolocation = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  /**
   * Handle "Use my location" — available directly on the input
   */
  const handleLocateClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't open modal
    setIsLocating(true);

    try {
      const result = await requestLocation({
        reverseGeocode: true,
        allowPrompt: true,
      });

      if (result?.address) {
        const displayValue =
          result.address.displayName || result.address.address1 || "";
        onChange(displayValue, result.address);
      }
    } catch (err) {
      console.error("Failed to get location:", err);
    } finally {
      setIsLocating(false);
    }
  }, [requestLocation, onChange]);

  const hasValue = value && value.length > 0;
  const canShowLocateButton =
    showLocateButton && geoSupported && !permissionDenied;

  const getRightSection = () => {
    const elements: React.ReactNode[] = [];

    if (isLocating || locationLoading) {
      elements.push(<Loader size={18} key="loader" />);
    } else {
      if (hasValue && !disabled) {
        elements.push(
          <CloseButton
            size={size}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            aria-label="Clear address"
            key="clear"
          />
        );
      }

      if (canShowLocateButton && !disabled) {
        elements.push(
          <Tooltip label={t(locateButtonTooltip)} key="locate">
            <ActionIcon
              variant="subtle"
              size={size}
              onClick={handleLocateClick}
              loading={isLocating || locationLoading}
              aria-label={t(locateButtonTooltip)}
            >
              <IconCurrentLocation size={18} />
            </ActionIcon>
          </Tooltip>
        );
      }
    }

    if (elements.length === 0) return null;
    if (elements.length === 1) return elements[0];

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {elements}
      </div>
    );
  };

  return (
    <>
      <TextInput
        label={label}
        placeholder={placeholder}
        value={value}
        readOnly
        onClick={() => {
          if (!disabled) {
            setModalOpened(true);
          }
        }}
        rightSection={getRightSection()}
        rightSectionPointerEvents="auto"
        rightSectionWidth={canShowLocateButton ? (hasValue ? 70 : 36) : undefined}
        error={error}
        required={required}
        disabled={disabled}
        size={size}
        styles={{
          input: {
            cursor: disabled ? "not-allowed" : "pointer",
            caretColor: "transparent",
          },
        }}
      />

      <AddressPickerModal
        opened={modalOpened}
        onClose={handleModalClose}
        onSelect={handleModalSelect}
        initialValue={value}
        customAddresses={customAddresses}
        locationBias={locationBias}
        placeholder={placeholder}
        showLocateButton={showLocateButton}
        locateButtonTooltip={t(locateButtonTooltip)}
        onRequestGeolocation={handleRequestGeolocation}
      />
    </>
  );
};

// ============================================
// Desktop Sub-Component: Standard Combobox with dropdown
// ============================================

const AddressAutocompleteDesktop: React.FC<AddressAutocompleteInputProps> = ({
  value,
  onChange,
  customAddresses = [],
  locationBias = null,
  placeholder = "Search for an address",
  label = "Address",
  size = "md",
  error,
  required,
  disabled,
  onFocus,
  showLocateButton = true,
  locateButtonTooltip = "common:buttons.useMyLocation",
  withinPortal = false,
}) => {
  const { t } = useTranslation(["common"]);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const {
    addressPredictions,
    loading,
    fetchAddressPredictions,
    fetchAddressDetails,
    geocodeAddress,
    clearAddressPredictions,
  } = useAddressAutocomplete();

  const {
    requestLocation,
    isLoading: locationLoading,
    permissionDenied,
    isSupported: geoSupported,
  } = useLocationHistoryContext();

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  /**
   * Track whether the current value has confirmed coordinates.
   * Set to true when user selects from dropdown, uses locate, etc.
   * Set to false when user types manually.
   * Used by blur handler to decide whether to forward-geocode.
   */
  const hasConfirmedAddressRef = React.useRef(false);

  const locationBiasRef = React.useRef(locationBias);
  React.useEffect(() => {
    locationBiasRef.current = locationBias;
  }, [locationBias]);

  /**
   * Handle input text change — manual typing.
   */
  const handleInputChange = useCallback(
    (newValue: string) => {
      hasConfirmedAddressRef.current = false;
      onChange(newValue, null);

      if (newValue && newValue.trim().length >= MIN_SEARCH_LENGTH) {
        fetchAddressPredictions(newValue, locationBiasRef.current);
        combobox.openDropdown();
      } else if (newValue) {
        clearAddressPredictions();
        combobox.openDropdown();
      } else {
        clearAddressPredictions();
        combobox.closeDropdown();
      }

      combobox.updateSelectedOptionIndex();
    },
    [onChange, fetchAddressPredictions, clearAddressPredictions, combobox]
  );

  /**
   * Handle clear button click.
   * Explicitly notifies parent with empty string and null fullAddress
   * so the parent can clear stored coordinates / location preview.
   */
  const handleClear = useCallback(() => {
    hasConfirmedAddressRef.current = false;
    onChange("", null);
    clearAddressPredictions();
    combobox.closeDropdown();
  }, [onChange, clearAddressPredictions, combobox]);

  /**
   * Mark address as confirmed and notify parent with full address data.
   */
  const confirmAddress = useCallback(
    (displayValue: string, fullAddress: AddressData) => {
      hasConfirmedAddressRef.current = true;
      onChange(displayValue, fullAddress);
    },
    [onChange]
  );

  /**
   * Handle custom address selection
   */
  const handleCustomAddressSelect = useCallback(
    (address: AddressData) => {
      const displayValue = address.address1 || "";
      confirmAddress(displayValue, address);
      combobox.closeDropdown();
    },
    [confirmAddress, combobox]
  );

  /**
   * Handle Google prediction selection
   */
  const handleGooglePredictionSelect = useCallback(
    async (addressId: string, displayName: string) => {
      setIsLoadingDetails(true);

      try {
        const fullAddress = await fetchAddressDetails(addressId);

        if (fullAddress) {
          confirmAddress(fullAddress.address1 || displayName, fullAddress);
        } else {
          hasConfirmedAddressRef.current = false;
          onChange(displayName, null);
        }
      } catch (err) {
        console.error("Failed to fetch address details:", err);
        hasConfirmedAddressRef.current = false;
        onChange(displayName, null);
      } finally {
        setIsLoadingDetails(false);
        combobox.closeDropdown();
      }
    },
    [fetchAddressDetails, confirmAddress, onChange, combobox]
  );

  /**
   * Handle "Use my location" button click
   */
  const handleLocateClick = useCallback(async () => {
    setIsLocating(true);

    try {
      const result = await requestLocation({
        reverseGeocode: true,
        allowPrompt: true,
      });

      if (result?.address) {
        const displayValue =
          result.address.displayName || result.address.address1 || "";
        confirmAddress(displayValue, result.address);
      }
    } catch (err) {
      console.error("Failed to get location:", err);
    } finally {
      setIsLocating(false);
    }
  }, [requestLocation, confirmAddress]);

  /**
   * Handle input focus.
   * Proactively requests geolocation so location bias is available
   * for Google suggestions without user needing to tap the locate button.
   */
  const handleFocus = useCallback(() => {
    onFocus?.();

    const hasPredictions =
      addressPredictions.length > 0 || customAddresses.length > 0;
    if (value && value.trim().length >= MIN_SEARCH_LENGTH && hasPredictions) {
      combobox.openDropdown();
    }
  }, [onFocus, value, combobox, addressPredictions.length, customAddresses.length]);

  /**
   * Handle input blur.
   * On desktop, if the user typed an address without selecting from suggestions,
   * attempt forward geocoding to resolve coordinates.
   */
  const handleBlur = useCallback(async () => {
    combobox.closeDropdown();

    // Already confirmed via selection — no need to geocode
    if (hasConfirmedAddressRef.current) {
      return;
    }

    const currentValue = value;

    // Empty value — nothing to geocode
    if (!currentValue || currentValue.trim().length === 0) {
      return;
    }

    // Too short for meaningful geocoding
    if (currentValue.trim().length < MIN_GEOCODE_LENGTH) {
      return;
    }

    try {
      const result = await geocodeAddress(currentValue);
      if (result) {
        hasConfirmedAddressRef.current = true;
        onChange(currentValue, result);
      }
    } catch (err) {
      console.error("Forward geocode on blur failed:", err);
    }
  }, [combobox, value, geocodeAddress, onChange]);

  /**
   * Handle option submit
   */
  const handleOptionSubmit = useCallback(
    (val: string) => {
      const customAddress = customAddresses.find(
        (addr) => addr.addressId === val
      );
      if (customAddress) {
        handleCustomAddressSelect(customAddress);
      } else {
        const prediction = addressPredictions.find(
          (pred) => pred.addressId === val
        );
        if (prediction) {
          handleGooglePredictionSelect(
            prediction.addressId,
            prediction.displayName
          );
        }
      }
    },
    [
      customAddresses,
      addressPredictions,
      handleCustomAddressSelect,
      handleGooglePredictionSelect,
    ]
  );

  const hasCustomAddresses = customAddresses.length > 0;
  const hasGooglePredictions = addressPredictions.length > 0;
  const showDropdown = hasCustomAddresses || hasGooglePredictions;
  const hasValue = value && value.length > 0;
  const isLoading = loading || isLoadingDetails || isLocating;

  const canShowLocateButton =
    showLocateButton && geoSupported && !permissionDenied;

  const getRightSection = useCallback(() => {
    const elements: React.ReactNode[] = [];

    if (isLoading) {
      elements.push(<Loader size={18} key="loader" />);
    } else {
      if (hasValue && !disabled) {
        elements.push(
          <CloseButton
            size={size}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            aria-label="Clear address"
            key="clear"
          />
        );
      }

      if (canShowLocateButton && !disabled) {
        elements.push(
          <Tooltip label={t(locateButtonTooltip)} key="locate">
            <ActionIcon
              variant="subtle"
              size={size}
              onClick={handleLocateClick}
              loading={isLocating || locationLoading}
              aria-label={t(locateButtonTooltip)}
            >
              <IconCurrentLocation size={18} />
            </ActionIcon>
          </Tooltip>
        );
      }
    }

    if (elements.length === 0) return null;
    if (elements.length === 1) return elements[0];

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {elements}
      </div>
    );
  }, [
    isLoading,
    hasValue,
    disabled,
    size,
    handleClear,
    canShowLocateButton,
    locateButtonTooltip,
    handleLocateClick,
    isLocating,
    locationLoading,
    t,
  ]);

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      disabled={disabled}
      withinPortal={withinPortal}
      zIndex={withinPortal ? 10000 : undefined}
      position="bottom"
      middlewares={{ flip: true, shift: true }}
      floatingStrategy={withinPortal ? "fixed" : "absolute"}
    >
      <Combobox.Target>
        <TextInput
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={(event) => handleInputChange(event.currentTarget.value)}
          onClick={() => hasValue && combobox.openDropdown()}
          onFocus={handleFocus}
          onBlur={handleBlur}
          error={error}
          required={required}
          disabled={disabled}
          rightSection={getRightSection()}
          rightSectionWidth={canShowLocateButton && hasValue ? 70 : undefined}
          size={size}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {!showDropdown &&
            hasValue &&
            value.length >= MIN_SEARCH_LENGTH &&
            !loading && <Combobox.Empty>No addresses found</Combobox.Empty>}

          {!showDropdown &&
            hasValue &&
            value.length < MIN_SEARCH_LENGTH &&
            !loading && (
              <Combobox.Empty>
                Type at least {MIN_SEARCH_LENGTH} characters to search
              </Combobox.Empty>
            )}

          {hasCustomAddresses && (
            <Combobox.Group label="Saved Addresses">
              {customAddresses.map((address) => (
                <Combobox.Option
                  value={address.addressId}
                  key={address.addressId}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <IconMapPin size={16} />
                    <div>
                      <div>{address.displayName || address.address1}</div>
                      {address.address1 &&
                        address.displayName !== address.address1 && (
                          <div style={{ fontSize: "12px", opacity: 0.6 }}>
                            {address.address1}
                            {address.city && `, ${address.city}`}
                            {address.region && `, ${address.region}`}
                          </div>
                        )}
                    </div>
                  </div>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {hasGooglePredictions && (
            <Combobox.Group
              label={hasCustomAddresses ? "Search Results" : undefined}
            >
              {addressPredictions.map((prediction) => (
                <Combobox.Option
                  value={prediction.addressId}
                  key={prediction.addressId}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <IconMapPin size={16} />
                    <div>{prediction.displayName}</div>
                  </div>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};